import type { Webhook } from '@/model/WebhookConfiguration.model';
import { copyText, successToast } from '@/helpers';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { RiEyeLine, RiEyeOffLine, RiFileCopyLine, RiRefreshLine } from 'react-icons/ri';
import styles from './WebhookSigningSecret.module.scss';

interface WebhookSigningSecretProps {
  closeModal: () => void;
  webhook: Webhook;
  onUpdate?: () => void;
}

/**
 * Modal for viewing, copying, and regenerating the signing secret for a webhook.
 * A regenerated secret only takes effect after saving via the dedicated signing-secret endpoint.
 */
export const WebhookSigningSecret: FC<WebhookSigningSecretProps> = ({ closeModal, webhook, onUpdate }) => {
  const { response: secretResponse, loading: isFetching } = useApiHook({
    endpoint: 'GET /api/v1/webhooks/{webhookId}/signing-secret',
    path: { webhookId: webhook.api_id },
    options: { onError: { toast: true, slug: 'fetch-signing-secret' } },
  });

  const { invoke: updateSecret, loading: isSaving } = useApiHook({
    endpoint: 'PATCH /api/v1/webhooks/{webhookId}/signing-secret',
    invokeOnInit: false,
    options: { onError: { toast: true, throw: true, slug: 'update-signing-secret' } },
  });

  const fetchedSecret = secretResponse?.body?.signing_secret ?? '';

  const [regeneratedSecret, setRegeneratedSecret] = useState<string | null>(null);
  const [secretVisible, setSecretVisible] = useState(false);

  const displayedSecret = regeneratedSecret ?? fetchedSecret;

  const isSubmitDisabled = regeneratedSecret == null || isSaving;

  const handleRegenerateSecret = useCallback(() => {
    const bytes = crypto.getRandomValues(new Uint8Array(30));
    const newSecret = Array.from(bytes, (b) => b.toString(36).padStart(2, '0'))
      .join('')
      .slice(0, 30);
    setRegeneratedSecret(newSecret);
    setSecretVisible(true);
  }, []);

  const handleCopySecret = useCallback(() => {
    copyText(displayedSecret, 'Signing secret copied to clipboard');
  }, [displayedSecret]);

  const handleSave = useCallback(async () => {
    if (regeneratedSecret == null) {
      return;
    }
    try {
      await updateSecret({
        path: { webhookId: webhook.api_id },
        body: { signing_secret: regeneratedSecret },
      } as Record<string, unknown>);
      onUpdate?.();
      closeModal();
      successToast('Signing secret updated');
    } catch {
      // Error already handled by useApiHook (toast + exception capture)
    }
  }, [updateSecret, webhook.api_id, regeneratedSecret, onUpdate, closeModal]);

  return (
    <ModalContainer size="narrow">
      <ModalHeader
        title="Signing Secret"
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
        subText="Use this secret to verify that webhook payloads were sent by In Vivo. Regenerating creates a new secret - save to apply it."
        subTextClasses="f6 lh-copy mt2 measure-wide"
      />
      <div className="pv3 ph2">
        <div className="mb3 ph2">
          <div className={styles.signingSecret}>
            <input
              type={secretVisible ? 'text' : 'password'}
              value={displayedSecret}
              readOnly
              aria-label="Signing secret"
              data-testid="signing-secret-input"
            />
            <button
              type="button"
              className={styles.secretAction}
              onClick={() => setSecretVisible((v) => !v)}
              disabled={isFetching}
              data-testid="toggle-secret-button"
              title={secretVisible ? 'Hide secret' : 'Show secret'}
              aria-label={secretVisible ? 'Hide secret' : 'Show secret'}
            >
              {secretVisible ? <RiEyeOffLine size={16} /> : <RiEyeLine size={16} />}
            </button>
            <button
              type="button"
              className={styles.secretAction}
              onClick={handleCopySecret}
              disabled={isFetching}
              data-testid="copy-secret-button"
              title="Copy to clipboard"
              aria-label="Copy to clipboard"
            >
              <RiFileCopyLine size={16} />
            </button>
            <button
              type="button"
              className={styles.secretAction}
              onClick={handleRegenerateSecret}
              disabled={isFetching}
              data-testid="regenerate-secret-button"
              title="Regenerate secret"
              aria-label="Regenerate secret"
            >
              <RiRefreshLine size={16} />
            </button>
          </div>
        </div>
      </div>
      <ModalActions
        className="pa3 bt b--moon-gray"
        onSubmit={handleSave}
        onCancel={closeModal}
        submitBtnText="Save"
        cancelBtnText="Cancel"
        submitButtonProps={{
          disabled: isSubmitDisabled,
          loading: isSaving,
        }}
      />
    </ModalContainer>
  );
};
